import {Injectable} from '@angular/core';
import * as signalR from '@microsoft/signalr';
import {HttpTransportType} from '@microsoft/signalr';

@Injectable({
  providedIn: 'root'
})
export abstract class SignalrService {

  private _conn?: signalR.HubConnection;

  constructor(
    private readonly url: string,
  ) { }

  private _startPromise?: Promise<void>;

  public startIfNot(accessToken: string): Promise<void> {
    if (!this._startPromise) {
      this._conn = new signalR.HubConnectionBuilder()
        .withUrl(this.url, { accessTokenFactory: () => accessToken, transport: HttpTransportType.LongPolling })
        .build();
      this._startPromise = this._conn.start();

      this._conn.onclose(async () => {
        console.log('SignalR connection close')
      });
    }
    return this._startPromise;
  }

  get connectionId(): any {
    return this._conn?.connectionId;
  }

  get connectionState(): any {
    return this._conn?.state;
  }

  /** Invokes a hub method on the server using the specified name and arguments.
   *
   * The Promise returned by this method resolves when the server indicates it has finished invoking the method. When the promise
   * resolves, the server has finished invoking the method. If the server method returns a result, it is produced as the result of
   * resolving the Promise.
   *
   * @typeparam T The expected return type.
   * @param {string} methodName The name of the server method to invoke.
   * @param {any[]} args The arguments used to invoke the server method.
   * @returns {Promise<T>} A Promise that resolves with the result of the server method (if any), or rejects with an error.
   */
  public invoke<T = any>(methodName: string, ...args: any[]): Promise<T> {
    return this._conn!.invoke(methodName, ...args);
  }

  /** Registers a handler that will be invoked when the hub method with the specified method name is invoked.
   *
   * @param {string} methodName The name of the hub method to define.
   * @param {Function} newMethod The handler that will be raised when the hub method is invoked.
   */
  public on(methodName: string, newMethod: (...args: any[]) => any): void {
    this._conn!.on(methodName, newMethod);
  }

  /** Removes all handlers for the specified hub method.
   *
   * @param {string} methodName The name of the method to remove handlers for.
   */
  public offAll(methodName: string): void {
    this._conn!.off(methodName);
  }

  /** Removes the specified handler for the specified hub method.
   *
   * You must pass the exact same Function instance as was previously passed to {@link @microsoft/signalr.HubConnection.on}. Passing a different instance (even if the function
   * body is the same) will not remove the handler.
   *
   * @param {string} methodName The name of the method to remove handlers for.
   * @param {Function} method The handler to remove. This must be the same Function instance as the one passed to {@link @microsoft/signalr.HubConnection.on}.
   */
  public off(methodName: string, method: (...args: any[]) => void): void {
    this._conn!.off(methodName, method);
  }
}
